import { useQuery } from "@tanstack/react-query";
import { Question, SurveyModel } from "survey-core";
import { Calendar } from "@rumbledirect/ui";
import dayjs, { Dayjs } from "dayjs";
import { Button, Spin, theme, Typography } from "antd";
import { useEffect, useReducer } from "react";

const RESOLVED_PROPS = [
  ["servicePartner", "servicePartnerId"],
  ["dealership", "dealershipId"],
  ["company", "companyId"],
  ["glassDamageLocation", "glassDamageLocationValue"],
  ["glassDamageType", "glassDamageTypeValue"],
  ["numberOfDamagePoints", "numberOfDamagePointsValue"],
  ["dossierType", "dossierTypeValue"],
  ["jobType", "jobTypeValue"],
] as const;

const RESOLVED_PROP_NAMES = RESOLVED_PROPS.map(([, resolved]) => resolved);

const DEFAULT_DOSSIER_TYPE = "repair_direct.generic";

const toIdString = (value: unknown): string | null => {
  if (value === null || value === undefined || value === "") return null;
  return String(value);
};

interface TimeSlot {
  id: string | null;
  start: string | null;
  end: string | null;
  amount: number;
}

// ATG (and other remote) slots have no plan-item id. Derive a stable, collision-free
// selection key from the date and start time so the slot can still be selected, keyed and
// highlighted. Database-backed slots keep using their plan-item id.
const getSlotKey = (dateIso: string, slot: TimeSlot): string =>
  slot.id ?? `${dateIso}T${slot.start ?? ""}`;

interface AppointmentDateItem {
  date: string;
  timeSlots: TimeSlot[];
}

interface AppointmentTimeSlotPlan {
  items?: AppointmentDateItem[];
}

interface AppointmentTimeSlotsResponse {
  member?: Array<{ items?: AppointmentDateItem[] }>;
  items?: AppointmentDateItem[];
}

const getAppointmentItems = (
  data: AppointmentTimeSlotsResponse | AppointmentTimeSlotPlan[] | undefined,
): AppointmentDateItem[] => {
  if (!data) return [];
  if (Array.isArray(data)) {
    return data[0]?.items ?? [];
  }

  if (!data.member) {
    return data.items ?? [];
  }

  // TODO: Logic to select the correct appointment time slot plan if multiple are returned.
  return data.member[0]?.items ?? [];
};

const formatTimeSlotLabel = (slot: TimeSlot): string => {
  if (!slot.end) return slot.start ?? "";
  return `${slot.start} - ${slot.end}`;
};

const parseStoredDate = (stored: string | null): string | null => {
  if (!stored) return null;
  // Extract just the date part (YYYY-MM-DD) from stored value.
  // Handles e.g. "2026-06-30", "2026-06-30 08:00:00" and "2026-06-30T08:00:00+02:00".
  return stored.split(/[ T]/)[0] ?? null;
};

export class AppointmentTimeSlotQuestionModel extends Question {
  constructor(name: string) {
    super(name);
    for (const [expressionProp, resolvedProp] of RESOLVED_PROPS) {
      this.addExpressionProperty(expressionProp, (_obj, result) => {
        this.setPropertyValue(resolvedProp, toIdString(result));
      });
    }
  }

  getType() {
    return "appointment-time-slot";
  }

  get servicePartnerId(): string | null {
    return (this.getPropertyValue("servicePartnerId") as string) ?? null;
  }
  get dealershipId(): string | null {
    return (this.getPropertyValue("dealershipId") as string) ?? null;
  }
  get companyId(): string | null {
    return (this.getPropertyValue("companyId") as string) ?? null;
  }
  get glassDamageLocationValue(): string | null {
    return (
      (this.getPropertyValue("glassDamageLocationValue") as string) ?? null
    );
  }
  get glassDamageTypeValue(): string | null {
    return (this.getPropertyValue("glassDamageTypeValue") as string) ?? null;
  }
  get numberOfDamagePointsValue(): string | null {
    return (
      (this.getPropertyValue("numberOfDamagePointsValue") as string) ?? null
    );
  }
  get dossierTypeValue(): string {
    return (
      (this.getPropertyValue("dossierTypeValue") as string) ??
      DEFAULT_DOSSIER_TYPE
    );
  }
  get jobTypeValue(): string | null {
    return (this.getPropertyValue("jobTypeValue") as string) ?? null;
  }
  get hasTimeSlots(): boolean {
    return (this.getPropertyValue("hasTimeSlots") as boolean) ?? false;
  }
}

interface AppointmentTimeSlotQuestionProps {
  question: AppointmentTimeSlotQuestionModel;
}

export const AppointmentTimeSlotQuestion = (
  props: AppointmentTimeSlotQuestionProps,
) => {
  const { token } = theme.useToken();
  const survey = props.question.survey as SurveyModel;

  const [, forceRender] = useReducer((x: number) => x + 1, 0);
  useEffect(() => {
    const question = props.question;
    question.registerFunctionOnPropertiesValueChanged(
      RESOLVED_PROP_NAMES as unknown as string[],
      forceRender,
      "appointment-time-slot-resolved",
    );
    return () => {
      question.unRegisterFunctionOnPropertiesValueChanged(
        RESOLVED_PROP_NAMES as unknown as string[],
        "appointment-time-slot-resolved",
      );
    };
  }, [props.question]);

  const servicePartnerId = props.question.servicePartnerId;
  const dealershipId = props.question.dealershipId;
  const companyId = props.question.companyId;
  const glassDamageLocation = props.question.glassDamageLocationValue;
  const glassDamageType = props.question.glassDamageTypeValue;
  const numberOfDamagePoints = props.question.numberOfDamagePointsValue;
  const dossierType = props.question.dossierTypeValue;
  const jobType = props.question.jobTypeValue;
  const hasTimeSlots = props.question.hasTimeSlots;

  const appointmentTimeSlotsQuery = useQuery({
    queryKey: [
      "appointmentTimeSlots",
      servicePartnerId,
      companyId,
      dealershipId,
      dossierType,
      glassDamageLocation,
      glassDamageType,
      numberOfDamagePoints,
      jobType,
    ],
    queryFn: async () => {
      return survey!
        .api!.get(
          `/driver/appointment-time-slots/available-at-service-partner/${servicePartnerId}`,
          {
            params: {
              dossierType,
              companyId,
              dealershipId,
              ...(glassDamageLocation ? { glassDamageLocation } : {}),
              ...(glassDamageType ? { glassDamageType } : {}),
              ...(numberOfDamagePoints ? { numberOfDamagePoints } : {}),
              ...(jobType ? { jobType } : {}),
            },
          },
        )
        .then((response) => response.data);
    },
    enabled: !!servicePartnerId,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    select: getAppointmentItems,
  });

  const items = appointmentTimeSlotsQuery.data ?? [];
  const availableDates = items.map((item) => item.date);

  const dateKey = `${props.question.name}__date`;
  const selectedSlotId = toIdString(props.question.value);
  const selectedDateIsoFromSurvey = parseStoredDate(
    survey.getValue(dateKey) as string | null,
  );
  const selectedDateIsoFromSelectedSlot = selectedSlotId
    ? (items.find((item) =>
        item.timeSlots.some(
          (timeSlot) => getSlotKey(item.date, timeSlot) === selectedSlotId,
        ),
      )?.date ?? null)
    : null;
  const selectedDateIso =
    selectedDateIsoFromSurvey ?? selectedDateIsoFromSelectedSlot;
  const selectedDate = selectedDateIso ? dayjs(selectedDateIso) : undefined;

  const selectedDateTimeSlots =
    items.find((item) => item.date === selectedDateIso)?.timeSlots ?? [];

  // clear up value, when the previously selected date is no longer available due to a dependency change.
  useEffect(() => {
    if (!appointmentTimeSlotsQuery.isSuccess) return;
    if (selectedDateIso && !availableDates.includes(selectedDateIso)) {
      survey.setValue(dateKey, null);
      props.question.value = null;
    }
  }, [
    appointmentTimeSlotsQuery.isSuccess,
    appointmentTimeSlotsQuery.dataUpdatedAt,
    selectedDateIso,
    availableDates,
    dateKey,
    props.question,
    survey,
  ]);

  useEffect(() => {
    if (!selectedDateIso || !selectedSlotId) return;
    const selectedSlotStillExists = selectedDateTimeSlots.some(
      (slot) => getSlotKey(selectedDateIso, slot) === selectedSlotId,
    );

    if (!selectedSlotStillExists) {
      props.question.value = null;
    }
  }, [selectedDateIso, selectedSlotId, selectedDateTimeSlots, props.question]);

  useEffect(() => {
    if (!selectedDateIso) return;
    if (selectedDateIsoFromSurvey === selectedDateIso) return;

    if (hasTimeSlots && selectedSlotId) {
      const selectedSlot = selectedDateTimeSlots.find(
        (s) => getSlotKey(selectedDateIso, s) === selectedSlotId,
      );
      if (selectedSlot?.start) {
        const [hour, minute] = selectedSlot.start
          .split(":")
          .map((v) => Number.parseInt(v, 10));
        survey.setValue(
          dateKey,
          dayjs(selectedDateIso)
            .hour(hour)
            .minute(minute)
            .second(0)
            .format("YYYY-MM-DDTHH:mm:ssZ"),
        );
        forceRender();
        return;
      }
    }

    survey.setValue(dateKey, selectedDateIso);
    forceRender();
  }, [
    selectedDateIso,
    selectedDateIsoFromSurvey,
    hasTimeSlots,
    selectedSlotId,
    selectedDateTimeSlots,
    survey,
    dateKey,
  ]);

  if (appointmentTimeSlotsQuery.isLoading) {
    return (
      <div style={{ textAlign: "center", padding: "40px 0" }}>
        <Typography.Text
          style={{
            display: "block",
            marginBottom: 16,
            fontWeight: 500,
            color: token.colorTextSecondary,
          }}
        >
          Bezig met laden...
        </Typography.Text>
        <Spin />
      </div>
    );
  }
  if (!servicePartnerId) {
    return <p>Kies een hersteller om de beschikbaarheid te zien</p>;
  }

  if (appointmentTimeSlotsQuery.isError && !appointmentTimeSlotsQuery.data) {
    return (
      <p>
        Er is een fout opgetreden bij het laden van de beschikbare tijdslots.
      </p>
    );
  }

  const handleChange = (date: Dayjs) => {
    const dateStr = date.format("YYYY-MM-DD");

    survey.setValue(dateKey, dateStr);
    props.question.value = null;

    // Auto-select first time slot if hasTimeSlots is false
    if (!hasTimeSlots) {
      const timeSlotsForDate = items.find((item) => item.date === dateStr);
      const firstSlot = timeSlotsForDate?.timeSlots?.[0];
      if (firstSlot) {
        props.question.value = getSlotKey(dateStr, firstSlot);
        const dateTimeStr = `${dateStr} ${firstSlot.start}:00`;
        survey.setValue(dateKey, dateTimeStr);
      }
    }

    forceRender();
  };

  const handleTimeSlotClick = (slotKey: string) => {
    props.question.value = slotKey;
    const selectedSlot = selectedDateIso
      ? selectedDateTimeSlots.find(
          (s) => getSlotKey(selectedDateIso, s) === slotKey,
        )
      : undefined;
    if (selectedSlot?.start && selectedDateIso && hasTimeSlots) {
      const [hour, minute] = selectedSlot.start
        .split(":")
        .map((v) => Number.parseInt(v, 10));
      const dateTimeStr = dayjs(selectedDateIso)
        .hour(hour)
        .minute(minute)
        .second(0)
        .format("YYYY-MM-DDTHH:mm:ssZ");
      survey.setValue(dateKey, dateTimeStr);
    }
    forceRender();
  };

  return (
    <div>
      <Calendar
        value={selectedDate}
        onSelect={handleChange}
        disabledDate={(d) => !availableDates.includes(d.format("YYYY-MM-DD"))}
      />

      {selectedDateIso && hasTimeSlots && (
        <div style={{ marginTop: 16 }}>
          <Typography.Text
            style={{ display: "block", marginBottom: 8, fontWeight: 500 }}
          >
            Kies een tijdslot
          </Typography.Text>

          {selectedDateTimeSlots.length === 0 ? (
            <Typography.Text style={{ color: token.colorTextSecondary }}>
              Geen tijdsloten beschikbaar voor deze datum.
            </Typography.Text>
          ) : (
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 8,
              }}
            >
              {selectedDateTimeSlots.map((slot) => {
                const slotKey = getSlotKey(selectedDateIso, slot);
                return (
                  <Button
                    key={slotKey}
                    type={selectedSlotId === slotKey ? "primary" : "default"}
                    onClick={() => handleTimeSlotClick(slotKey)}
                  >
                    {formatTimeSlotLabel(slot)}
                  </Button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
